import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useState } from 'react'
import { CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { CreateButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import {
  CommitteeConfigurationForm,
  CommitteeConfigurationFormValue,
} from 'src/screens/administration/configuration-management/committees-configuration/edit/CommitteeConfigurationForm'
import { ROUTES } from 'src/routing/routes'
import { useParams } from 'react-router'
import { Utils } from 'src/shared/utils/Utils'
import { Committee_Config_Insert_Input, Mutation_Root, Query_Root } from 'src/@types/graphql'
import {
  insertCommitteeConfig,
  queryHighestCommitteeConfigSortNumber,
} from 'src/screens/administration/configuration-management/committees-configuration/committeeConfigurationQueries'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'

const defaultInitialValues: CommitteeConfigurationFormValue = {
  enDesc: '',
  deDesc: '',
  frDesc: '',
  itDesc: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  process: PROCESS.PF_KAP,
}

export const CommitteeConfigurationCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const { processType } = useParams()
  const process = Utils.getEnumFromString(processType, PROCESS)

  const [initialValues, setInitialValues] = useState<CommitteeConfigurationFormValue | null>(null)
  const [formDirty, setFormDirty] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    setInitialValues({
      ...defaultInitialValues,
      process: process,
    })
  }, [process])

  const onBackHandler = useCallback(() => {
    switch (process) {
      case 'PF_PGV':
        return navigate(ROUTES.CommitteesConfigurationIndex.nested.CommitteesConfigurationPfPgv.params({}))
      case 'PF_KAP':
        return navigate(ROUTES.CommitteesConfigurationIndex.nested.CommitteesConfigurationPfKap.params({}))
      case 'FACTSHEET':
        return navigate(ROUTES.CommitteesConfigurationIndex.nested.CommitteesConfigurationFactsheet.params({}))
      default:
        return ''
    }
  }, [navigate])

  const onCreate = async () => {
    document.getElementById('committee-config-form')?.dispatchEvent(
      new Event('submit', {
        cancelable: true,
        bubbles: true,
      }),
    )
  }

  const handleSubmitLocal = useCallback(
    async (values: CommitteeConfigurationFormValue) => {
      const { data, error } = await urqlClient
        .query<{
          committee_config_aggregate: Query_Root['committee_config_aggregate']
        }>(queryHighestCommitteeConfigSortNumber, {})
        .toPromise()

      if (error || !data?.committee_config_aggregate.aggregate?.max?.sort_number) {
        notificationService.operationFailed()
        return
      }

      const newSortNumber = data.committee_config_aggregate.aggregate.max.sort_number

      const insertInput: Committee_Config_Insert_Input = {
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        descriptions: {
          de: values.deDesc ? HtmlSanitizer.sanitize(values.deDesc) : '',
          en: values.enDesc ? HtmlSanitizer.sanitize(values.enDesc) : '',
          fr: values.frDesc ? HtmlSanitizer.sanitize(values.frDesc) : '',
          it: values.itDesc ? HtmlSanitizer.sanitize(values.itDesc) : '',
        },
        processes: [values.process],
        sort_number: newSortNumber + 1,
        code: crypto.randomUUID(),
      }

      const { data: dataInsert, error: errorInsert } = await urqlClient
        .mutation<{
          insert_committee_config: Mutation_Root['insert_committee_config']
        }>(insertCommitteeConfig, {
          committeeConfigInsertInput: insertInput,
        })
        .toPromise()

      if (!errorInsert && dataInsert?.insert_committee_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, urqlClient, onBackHandler, getMessage],
  )

  return (
    <ScreenLayout
      title={getMessage('label.configuration.committee.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <CommitteeConfigurationForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              formDirty={formDirty}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
